import React, { useState, useEffect } from "react";
import { Button, Box, Input, Modal } from "@sainsburys-tech/fable";
import Headerfile from "../../common/Headerfile";
import Footerfile from "../../common/Footerfile";
import Styles from "./EditPrinterModel.module.css";
import { useNavigate, useParams } from "react-router-dom";
import { useGetPostsQuery, usePutDataMutation } from "../../../api/apiConfig";
import { getLogisticsRole } from "../../../utils/authToken";
import { USER_ROLE } from "../../../constants/roles";
import Unauthorized from "../../unauthorized/Unauthorized";

const EditPrinterModel = () => {
  const { Id } = useParams();
  const [values, setValues] = useState({
    Id: "",
    Name: "",
    Image: "",
    ManufacturerName: ""
  });

  const [error, setError] = useState({
    Id: "",
    Name: "",
    Image: "",
    ManufacturerName: ""
  });

  const {
    isLoading,
    isSuccess,
    data: getData,
    refetch
  } = useGetPostsQuery(`printer-models/${Id}`);

  const [putData] = usePutDataMutation();
  const [apiHeading, setApiHeading] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);

  useEffect(() => {
    if (isSuccess && getData?.Model) {
      const model = getData.Model as { [key: string]: any };

      setValues({
        Id: model?.PrinterModelId ?? model?.Id,
        Name: model?.Name,
        Image: model?.Image,
        ManufacturerName: model?.ManufacturerName
      });
    } else {
      console.log("No data found or error occurred");
    }
  }, [isSuccess, getData]);

  useEffect(() => {
    refetch();
  }, []);

  const handleChange = (name: string, value: string | File) => {
    setValues({ ...values, [name]: value });
  };

  const loggedUserRole = getLogisticsRole();

  const isDisabled =
    loggedUserRole?.includes(USER_ROLE.ADMIN) ||
    loggedUserRole?.includes(USER_ROLE.READ_WRITE)
      ? false
      : true;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();

      reader.onloadend = () => {
        if (reader.result) {
          const base64String = reader.result.toString();

          setValues({
            ...values,
            Image: base64String
          });
        }
      };

      reader.readAsDataURL(file);
    }
  };

  interface IState {
    Id: string;
    Name: string;
    Image: string;
    ManufacturerName: string;
  }

  const initialState: IState = {
    Id: "",
    Name: "",
    Image: "",
    ManufacturerName: ""
  };

  interface IData {
    StatusCode: number;
    Status: string;
    TotalRecords?: number | null;
    Message?: string;
    Model?: any;
    Value?: string;
  }

  interface IResponse {
    data: IData;
    TotalRecords?: number | null;
  }

  function isIResponse(response: any): response is IResponse {
    return response.data !== undefined && typeof response.data === "object";
  }

  const isBlob = (value: any): value is Blob => {
    return value instanceof Blob;
  };

  const Validation = (values: any) => {
    let error = {
      Name: "",
      Id: "",
      printer: "",
      Image: "",
      ManufacturerName: ""
    };

    if (!values.Name) {
      error.printer = "Printer Required";
    } else if (values.Name.length < 5) {
      error.printer = "Printer not Correct";
    }
    if (!values.Image) {
      error.Image = "Printer Image Required";
    }

    if (values.Image) {
      let fileType = "";
      let fileSize = 0;

      if (typeof values.Image === "string") {
        if (values.Image.startsWith("data:")) {
          const mimeTypeMatch = values.Image.match(
            /^data:(image\/[a-zA-Z0-9-.+]+);base64,/
          );
          if (mimeTypeMatch) {
            fileType = mimeTypeMatch[1];
          }

          const base64StringLength =
            values.Image.length - values.Image.indexOf(",") - 1;
          fileSize = (base64StringLength * 3) / 4 / 1024 / 1024;
        } else if (values.Image.startsWith("/9j")) {
          fileType = "image/jpeg";
        } else {
          fileType = "image/png";
        }
      } else if (values.Image.type) {
        fileType = values.Image.type;
        fileSize = values.Image.size / 1024 / 1024;
      }

      const validImageTypes = ["image/jpeg", "image/png"];

      if (!validImageTypes.includes(fileType)) {
        error.Image = "Only JPG and PNG files are allowed";
      } else if (fileSize > 2) {
        error.Image = "File size should be less than 2MB";
      }
    }
    return error;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = Validation(values);
    setError(validationErrors);

    if (
      validationErrors.Name.length === 0 &&
      values.Name !== "" &&
      values.Id !== null &&
      validationErrors.Image.length === 0
    ) {
      setIsSaveButtonDisabled(true);
      if (isBlob(values.Image)) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          if (reader.result) {
            const base64String = reader.result
              .toString()
              .replace("data:", "")
              .replace(/^.+,/, "");

            try {
              const response = await putData({
                Method: "printer-models",
                ...values,
                PrinterModelId: values.Id,
                Image: base64String,
                ManufacturerName: values.ManufacturerName
              });
              if (
                isIResponse(response) &&
                response.data.StatusCode === 0 &&
                response.data.Status === "Ok"
              ) {
                setIsSaveButtonDisabled(false);
                setApiMessage("Printer model updated successfully" || null);
                setApiHeading("Success!!!");
                setIsOpen(true);
                setValues(initialState);
                setTimeout(() => {
                  navigate("/addprintermodel");
                }, 1500);
              } else if (isIResponse(response) && response.data) {
                setIsSaveButtonDisabled(false);
                setApiMessage(response.data.Message || null);
                setApiHeading("Error!!!");
                setIsOpen(true);
                console.error("Error response:", response);
              } else {
                setIsSaveButtonDisabled(false);
                console.error("Something Went Wrong..", response);
              }
            } catch (error) {
              setIsSaveButtonDisabled(false);
              console.error(error);
            }
          } else {
            console.error("FileReader result is null");
          }
        };
        reader.readAsDataURL(values.Image);
      } else {
        try {
          const response = await putData({
            Method: "printer-models",
            ...values,
            PrinterModelId: values.Id,
            Image: values.Image.replace("data:", "").replace(/^.+,/, "")
          });
          if (
            isIResponse(response) &&
            response.data.StatusCode === 0 &&
            response.data.Status === "Ok"
          ) {
            setIsSaveButtonDisabled(false);
            setApiMessage("Printer model updated successfully." || null);
            setApiHeading("Success!!!");
            setIsOpen(true);
            setValues(initialState);
            setTimeout(() => {
              navigate("/addprintermodel");
            }, 1500);
          } else if (isIResponse(response) && response.data) {
            setApiMessage(response.data.Message || null);
            setApiHeading("Error!!!");
            setIsSaveButtonDisabled(false);
            setIsOpen(true);

            console.error("Error response:", response);
          } else {
            setIsSaveButtonDisabled(false);
            console.error("Something Went Wrong..", response);
          }
        } catch (error) {
          setIsSaveButtonDisabled(false);
          console.error(error);
        }
      }
    }
  };

  return (
    <div>
      <div className={Styles.container}>
        <Headerfile />
        {isDisabled ? (
          <Unauthorized />
        ) : (
          <Box as="div" className={Styles.box}>
            <form onSubmit={handleSubmit} className={Styles.formContainer}>
              <h5 className={Styles.heading}>Edit Printer Model</h5>
              <p className={Styles.rightAlign}>
                <span className={Styles.redAsterisk}>*</span>Indicates required
                field
              </p>
              <label className={Styles.label}>
                <Input
                  name="printer"
                  label="Printer Model*"
                  autocomplete="off"
                  type="text"
                  value={values.Name}
                  onChange={(e) => handleChange("Name", e)}
                />
              </label>
              <span
                className={Styles.errormessage}
                style={{
                  display: values.Name.length > 0 ? "none" : "block"
                }}
              >
                {isLoading === true ? "" : "Printer Model is required"}
              </span>
              <br />
              <label className={`${Styles.label} ${Styles.fileLabel}`}>
                Printer Image*
              </label>
              {values.Image && (
                <div className={Styles.imagePreview}>
                  <img
                    src={
                      values.Image.startsWith("data:image/")
                        ? values.Image
                        : values.Image.startsWith("/9j/")
                        ? `data:image/jpeg;base64,${values.Image}`
                        : `data:image/png;base64,${values.Image}`
                    }
                    alt="Printer"
                    className={Styles.image}
                    height="20%"
                    width="30%"
                  />
                </div>
              )}
              <input
                name="Image"
                className={`${Styles.fileInput} form-control form-control-lg`}
                id="formFileLg"
                type="file"
                onChange={handleFileChange}
              />
              <span
                className={Styles.errormessage}
                style={{
                  display: error.Image ? "block" : "none"
                }}
              >
                {error.Image}
              </span>
              <br />
              <label className={Styles.label}>
                <Input
                  name="ManufacturerName"
                  label="Manufacturer"
                  autocomplete="off"
                  type="text"
                  value={values.ManufacturerName}
                  onChange={(e) => handleChange("ManufacturerName", e)}
                />
              </label>
              <br />
              <Button
                type="submit"
                className={Styles.button}
                disabled={isSaveButtonDisabled}
              >
                Update
              </Button>
            </form>
          </Box>
        )}
      </div>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen}
        layout="default"
        header={{
          isDismissible: true
        }}
      >
        <div className="text-center fw-400">
          <h3 className="text-center fw-500">{apiHeading}</h3>
          <h5 className="fw-400">{apiMessage}</h5>
        </div>
      </Modal>
      <Footerfile />
    </div>
  );
};

export default EditPrinterModel;
